import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useScheduleDetail, useSchedules } from './hooks/useSchedules';
import { Button, Badge, Card, FeaturePlaceholder, Alert, KPICard, Table, Avatar } from '@/components/ui';
import { ScheduleFormModal } from './components/ScheduleFormModal';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';

const statusColors: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-500',
  assigned: 'bg-blue-100 text-blue-600',
  in_progress: 'bg-amber-100 text-amber-600',
  completed: 'bg-emerald-100 text-emerald-600',
  delayed: 'bg-red-100 text-red-600',
  cancelled: 'bg-slate-200 text-slate-700',
  missed: 'bg-red-200 text-red-800',
};

const statusLabels: Record<string, string> = {
  draft: 'Bản nháp',
  assigned: 'Đã giao việc',
  in_progress: 'Đang thực hiện',
  completed: 'Đã hoàn thành',
  delayed: 'Bị trễ',
  cancelled: 'Đã hủy',
  missed: 'Bỏ lỡ',
};

const priorityColors: Record<string, string> = {
  low: 'bg-slate-100 text-slate-500',
  medium: 'bg-blue-100 text-blue-600',
  high: 'bg-amber-100 text-amber-600',
  critical: 'bg-red-100 text-red-600',
};

export default function MaintenanceScheduleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';

  const { data: item, isLoading } = useScheduleDetail(id);
  const { generateWorkOrder, isGeneratingWO } = useSchedules(companyId);

  const [activeTab, setActiveTab] = useState('overview');
  const [showEdit, setShowEdit] = useState(false);

  if (isLoading) return <div className="p-12 text-center text-slate-500 text-sm">Đang tải chi tiết lịch trình...</div>;
  if (!item) return <div className="p-12 text-center text-red-500 font-bold">Không tìm thấy dữ liệu lịch trình</div>;

  const handleGenerateWO = async () => {
    try {
      await generateWorkOrder(id!);
      toast.success('Đã khởi tạo lệnh công việc (Work Order) thành công');
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const tabs = [
    { value: 'overview', label: 'Tổng quan' },
    { value: 'resources', label: 'Nhân sự & Thiết bị' },
    { value: 'history', label: 'Nhật ký thay đổi' },
  ];

  return (
    <PageDetailLayout
      title={`Lịch trình: ${item.code}`}
      description={
        <div className="flex items-center gap-3 mt-1">
          <Badge className={`${statusColors[item.status_code] || 'bg-slate-100 text-slate-600'} border-transparent text-[10px] font-bold `}>
            {statusLabels[item.status_code] || item.status_code}
          </Badge>
          <span className="text-sm text-slate-400 font-mono">#{item.code}</span>
          <span className="text-slate-300">|</span>
          <Badge className={`${priorityColors[item.priority_code]} border-transparent text-[10px] font-bold `}>
            {item.priority_code}
          </Badge>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500 flex items-center gap-1">
            <i className="ri-calendar-line" />
            {new Date(item.scheduled_date).toLocaleDateString('vi-VN')}
          </span>
        </div>
      }
      breadcrumbs={[
        { label: 'Lịch bảo dưỡng', path: '/maintenance/schedules' },
        { label: item.code }
      ]}
      headerActions={
        <div className="flex gap-2">
          {!item.work_order_id && (
            <AuthButton
              module="maintenance"
              action="create"
              variant="primary"
              icon="ri-magic-line"
              onClick={handleGenerateWO}
              isLoading={isGeneratingWO}
            >
              Phát sinh Work Order
            </AuthButton>
          )}
          {item.work_order_id && (
            <Button variant="outline" icon="ri-external-link-line" onClick={() => navigate(`/maintenance/work-orders/detail/${item.work_order_id}`)}>
              Xem Work Order
            </Button>
          )}
          <AuthButton module="maintenance" action="update" variant="outline" icon="ri-edit-line" onClick={() => setShowEdit(true)}>
            Chỉnh sửa
          </AuthButton>
        </div>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {activeTab === 'overview' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KPICard title="Giờ dự tính" value={item.estimated_hours || 0} icon="ri-time-line" variant="info" suffix="h" />
                <KPICard title="Giờ thực tế" value={item.actual_hours || 0} icon="ri-timer-flash-line" variant="success" suffix="h" />
                <KPICard title="Thời gian bắt đầu" value={item.start_time || '--:--'} icon="ri-play-mini-line" variant="neutral" />
              </div>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <i className="ri-information-line text-os-primary" />
                  Thông tin lịch trình
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-12">
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Dự án</span>
                    <span className="text-sm font-bold text-slate-700">{item.project?.name || '---'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Khu vực (Zone)</span>
                    <span className="text-sm font-bold text-os-primary">{item.zone?.zone_name || '---'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Thuộc kế hoạch</span>
                    <span className="text-sm font-bold text-slate-700">{item.plan?.name || '---'}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-50 pb-2">
                    <span className="text-sm text-slate-500">Tổ thực hiện</span>
                    <span className="text-sm font-bold text-slate-700">{item.team?.name || 'Chưa gán tổ'}</span>
                  </div>
                </div>
                <div className="mt-6">
                  <label className="block text-[10px] font-bold text-slate-400  mb-2">Ghi chú vận hành</label>
                  <p className="text-sm text-slate-600 bg-slate-50 p-4 rounded-2xl border border-slate-100 italic">
                    {item.notes || 'Không có ghi chú đặc biệt cho lịch trình này.'}
                  </p>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <i className="ri-user-follow-line text-os-primary" />
                  Nhân sự phụ trách
                </h3>
                <div className="flex items-center gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                  <Avatar src={item.assignee?.avatar_url} name={item.assignee?.full_name} size="lg" />
                  <div>
                    <p className="text-sm font-bold text-slate-900">{item.assignee?.full_name || 'Chưa phân công cá nhân'}</p>
                    <p className="text-xs text-slate-500">Người chịu trách nhiệm chính tại hiện trường</p>
                  </div>
                </div>
              </Card>
            </>
          )}

          {activeTab === 'resources' && (
            <FeaturePlaceholder title="Tài nguyên vận hành" description="Danh sách thiết bị và vật tư đã xuất kho cho lịch trình này." />
          )}

          {activeTab === 'history' && (
            <FeaturePlaceholder title="Nhật ký thay đổi" description="Lịch sử cập nhật trạng thái và điều chỉnh lịch trình." />
          )}
        </div>

        <div className="space-y-6">
          <Card className="p-6 bg-slate-900 text-white">
            <h4 className="font-bold mb-4 flex items-center gap-2">
              <i className="ri-time-line text-os-primary" />
              Tiến độ thực hiện
            </h4>
            <div className="space-y-4">
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-400  font-bold tracking-wider">Trạng thái</span>
                <Badge className={`${statusColors[item.status_code]} border-transparent text-[10px] font-bold `}>
                  {statusLabels[item.status_code]}
                </Badge>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-400">Thời gian dự kiến</span>
                <span className="font-bold">{item.estimated_hours}h</span>
              </div>
              <div className="pt-4 border-t border-slate-800">
                {item.work_order_id ? (
                  <Alert variant="success" icon="ri-checkbox-circle-line">
                    Đã tạo Work Order #{item.work_order?.order_number} cho lịch trình này.
                  </Alert>
                ) : (
                  <Alert variant="warning" icon="ri-error-warning-line">
                    Cần phát sinh Work Order để bắt đầu ghi nhận thực tế.
                  </Alert>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>

      <ScheduleFormModal
        isOpen={showEdit}
        item={item}
        companyId={companyId}
        onClose={() => setShowEdit(false)}
      />
    </PageDetailLayout>
  );
}
